/**
 * MOC imbalance level.
 *
 * NYSE publishes market-on-close imbalances at 3:50 PM ET = 2:50 PM CT =
 * 12:50 PM PT. The MOC level is the open of the 12:50 PM PT candle, i.e. the
 * first trade at or after 14:50:00 America/Chicago. The chart always shows the
 * most recent one: it carries past the 17:00 CT roll, overnight and over the
 * weekend until the next 14:50 CT print replaces it.
 *
 * Source data: the store's raw candles (`marketStore.candles`), which are the
 * engine's base bars (BAR_INTERVAL, 2-min in prod) no matter what timeframe
 * the chart is showing. The client never keeps raw ticks, so the open of the
 * earliest base bar whose start is at/after 14:50:00 CT is the first trade at
 * or after 14:50 — exact whenever 14:50 sits on a bar boundary (true for 1/2/5
 * minute bars). A bar that merely straddles 14:50 is never used, because its
 * open printed before 14:50.
 *
 * All wall-clock maths goes through Intl with the real America/Chicago zone,
 * never a fixed UTC offset, so it is DST-safe.
 */

import type { Candle } from '@shared/types';

export const MOC_TIMEZONE = 'America/Chicago';
/** 14:50 CT — the 12:50 PM PT candle. */
export const MOC_HOUR_CT = 14;
export const MOC_MINUTE_CT = 50;
/**
 * The MOC window ends at the daily CME close (16:00 CT). A day with nothing
 * at/after 14:50 before then (half days close at 12:00/12:15 CT, holidays) has
 * no MOC of its own, so the previous day's level stays.
 */
export const MOC_WINDOW_END_HOUR_CT = 16;
/** CME session roll: bars from 17:00 CT belong to the next trading day. */
export const SESSION_ROLL_HOUR_CT = 17;

/** Chart + price-axis label. The fire emoji was explicitly asked for. */
export const MOC_LABEL = '\u{1F525} MOC';

export interface MocLevel {
  /** Open of the first bar at/after 14:50:00 CT. */
  price: number;
  /** Start time (epoch ms) of that bar. */
  timestamp: number;
  /** Trading day the level belongs to, `YYYY-MM-DD` (CT). */
  tradingDay: string;
}

interface WallParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const ctFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: MOC_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/** Wall-clock parts of an instant in America/Chicago. */
export function chicagoParts(ms: number): WallParts {
  const out: Record<string, number> = {};
  for (const p of ctFormatter.formatToParts(new Date(ms))) {
    if (p.type !== 'literal') out[p.type] = Number(p.value);
  }
  return {
    year: out.year,
    month: out.month,
    day: out.day,
    // Some engines render midnight as 24 even with h23.
    hour: out.hour === 24 ? 0 : out.hour,
    minute: out.minute,
    second: out.second,
  };
}

/** Offset (ms) of America/Chicago from UTC at the given instant. */
function chicagoOffsetMs(ms: number): number {
  const p = chicagoParts(ms);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(ms / 1000) * 1000;
}

/** Epoch ms of a Chicago wall-clock time (DST-correct). */
export function chicagoWallTimeToMs(
  year: number, month: number, day: number, hour: number, minute: number, second = 0,
): number {
  const guess = Date.UTC(year, month - 1, day, hour, minute, second);
  const first = guess - chicagoOffsetMs(guess);
  // Second pass settles the case where the guess and the answer straddle a
  // DST change (can't happen at 14:50, but keeps the helper honest).
  return guess - chicagoOffsetMs(first);
}

/**
 * CME trading day an instant belongs to. The session that opens at 17:00 CT
 * belongs to the next calendar day.
 */
export function tradingDayOf(ms: number): { year: number; month: number; day: number; key: string } {
  const p = chicagoParts(ms);
  let { year, month, day } = p;
  if (p.hour >= SESSION_ROLL_HOUR_CT) {
    const next = new Date(Date.UTC(year, month - 1, day + 1));
    year = next.getUTCFullYear();
    month = next.getUTCMonth() + 1;
    day = next.getUTCDate();
  }
  const key = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return { year, month, day, key };
}

/**
 * Most recent MOC level in the loaded data: the latest trading day that has a
 * print at/after 14:50:00 CT (before 16:00 CT). The level carries forward
 * through the 17:00 CT roll, the overnight session, the next morning and the
 * weekend until a newer 14:50 print replaces it. Days with no print in the
 * window (half days, holidays) are skipped, so the prior MOC stays. Null only
 * when no loaded day has a MOC print at all.
 *
 * `candles` must be ascending by timestamp (the store's invariant). The scan
 * runs backwards one trading day at a time; Intl is only consulted once per
 * day crossed, not once per bar, so the usual case (MOC within the last
 * session or two) touches a few hundred bars.
 */
export function computeMocLevel(candles: readonly Candle[] | null | undefined): MocLevel | null {
  if (!candles || candles.length === 0) return null;

  let i = candles.length - 1;
  while (i >= 0) {
    const ts = candles[i].timestamp;
    if (!Number.isFinite(ts)) { i--; continue; }

    const td = tradingDayOf(ts);
    const start = chicagoWallTimeToMs(td.year, td.month, td.day, MOC_HOUR_CT, MOC_MINUTE_CT, 0);
    const end = chicagoWallTimeToMs(td.year, td.month, td.day, MOC_WINDOW_END_HOUR_CT, 0, 0);
    // The session this trading day belongs to opened at 17:00 CT the day before.
    const prev = new Date(Date.UTC(td.year, td.month - 1, td.day - 1));
    const sessionStart = chicagoWallTimeToMs(
      prev.getUTCFullYear(), prev.getUTCMonth() + 1, prev.getUTCDate(), SESSION_ROLL_HOUR_CT, 0, 0,
    );

    // Bars of this trading day at/after 14:50 CT; keep the earliest in-window one.
    let found: Candle | null = null;
    for (; i >= 0 && candles[i].timestamp >= start; i--) {
      const c = candles[i];
      if (c.timestamp < end && c.open > 0) found = c;
    }
    if (found) return { price: found.open, timestamp: found.timestamp, tradingDay: td.key };

    // No MOC this day: skip the rest of its session and try the day before.
    while (i >= 0 && candles[i].timestamp >= sessionStart) i--;
  }
  return null;
}
